import { Button, Card, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { RiAddFill } from "react-icons/ri";
import { EmptyState } from "src/components/EmptyState";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { useFormTemplateContext } from "../../formTemplateContext";
import { GET_SCHOOLS_FOR_FORM_TEMPLATE_EDIT } from "../../graphql/queries";
import { AddQuestionButton } from "./components/AddQuestionButton";
import { AddSectionButton } from "./components/AddSectionButton";
import { SectionChangesIndicator } from "./components/ChangesIndicator";
import { ImportError } from "./components/ImportError";
import { QuestionForm } from "./components/QuestionForm";
import { findRelatedQuestionVisibilityByQuestionId } from "./components/QuestionVisibility/helpers";
import { DisclaimerForm } from "./components/SectionForm/DisclaimerForm";
import { SchoolRankingForm } from "./components/SectionForm/SchoolRankingForm";
import { SectionForm } from "./components/SectionForm/SectionForm";
import { ValidationAlert } from "./components/ValidationAlert";
import { useEditFormTemplateContext } from "./context";
import { State } from "./types/state";

interface Props {
  section: AF.V2.Section;
  organizationId: uuid;
  order: number;
}

export const FormTemplateSection = ({
  section,
  order,
}: Props): React.ReactElement => {
  const { mode } = useFormTemplateContext();
  const { dispatch, state, formTemplateId } = useEditFormTemplateContext();

  const { remoteData: schoolsResult } = useRemoteDataQuery<
    GQL.GetSchoolsForFormTemplateEdit,
    GQL.GetSchoolsForFormTemplateEditVariables
  >(GET_SCHOOLS_FOR_FORM_TEMPLATE_EDIT, {
    variables: {
      formTemplateId: formTemplateId ?? "",
    },
    skip: formTemplateId === undefined,
    fetchPolicy: "no-cache",
  });

  const schools = React.useMemo(() => {
    return schoolsResult.map((result) => {
      return result.school.map((school) => ({
        id: school.id,
        label: school.name,
        reference_id: school.reference_id,
      }));
    });
  }, [schoolsResult]);

  const addQuestion = React.useCallback(
    (atIndex: number) => () => {
      dispatch({
        type: "AddQuestion",
        sectionId: section.id,
        atIndex,
      });
    },
    [dispatch, section.id]
  );

  const onQuestionUpdateHandler = React.useCallback(
    async (
      question: AF.V2.Question,
      verification: AF.V2.FormVerification | undefined,
      deletedVerificationIds: string[] | undefined
    ) => {
      if (verification) {
        dispatch({
          type: "UpdateVerification",
          verification: verification,
        });
      }
      dispatch({
        type: "UpdateQuestion",
        sectionId: section.id,
        question: question,
      });
      if (deletedVerificationIds) {
        dispatch({
          type: "RemoveVerifications",
          verificationIds: deletedVerificationIds,
        });
      }
    },
    [dispatch, section.id]
  );

  const { confirm, confirmationDialog } = useConfirmationDialog({
    body: "This will remove the question and any changes that have been made to it.",
    header: "Remove question?",
    confirmButton: {
      label: "Yes, remove question",
    },
  });
  const onRemoveHandler = React.useCallback(
    async (question: AF.V2.Question) => {
      let body =
        "This will remove the question and any changes that have been made to it.";
      if (question.key) {
        // check if this question is being used for question visibility
        const otherQuestions = getOtherQuestions(state, question.id);
        const relatedQuestions = question.key
          ? findRelatedQuestionVisibilityByQuestionId(
              otherQuestions,
              question.id
            )
          : [];
        if (relatedQuestions.length > 0) {
          body +=
            " If you remove this question, other questions conditionally revealed by this one this may no longer be revealed.";
        }
      }

      if (await confirm({ body })) {
        dispatch({
          type: "RemoveQuestion",
          sectionId: section.id,
          question,
        });
      }
    },
    [confirm, state, dispatch, section.id]
  );

  const onArchiveHandler = React.useCallback(
    async (question: AF.V2.Question) => {
      const body = (
        <Flex direction="column" gap="4">
          <Text>
            This will archive the question, <b>hiding it completely</b> from
            responses on both Staff and Family sides. Data will not be deleted
            and will continue to be visible in exports.
          </Text>
          <Text>
            Any <b>conditionality</b> associated with this question will be
            removed, and may require manual fixing. This can't be undone or
            recovered.
          </Text>
        </Flex>
      );

      if (
        await confirm({
          body,
          header: "Archive question?",
          confirmButton: {
            label: "Yes, archive question",
          },
        })
      ) {
        dispatch({
          type: "ArchiveQuestion",
          sectionId: section.id,
          question,
        });
      }
    },
    [confirm, dispatch, section.id]
  );

  const onUnarchiveHandler = React.useCallback(
    async (question: AF.V2.Question) => {
      if (
        await confirm({
          body: "This will unarchive the question, making the answers visible again on both Staff and Family sides.",
          header: "Unarchive question?",
          confirmButton: {
            label: "Yes, unarchive question",
          },
        })
      ) {
        dispatch({
          type: "UnarchiveQuestion",
          sectionId: section.id,
          question,
        });
      }
    },
    [confirm, dispatch, section.id]
  );

  const sectionElement = React.useMemo(() => {
    switch (section.type) {
      case AF.V2.SectionTypes.PreRankingSection:
      case AF.V2.SectionTypes.GeneralSection: {
        return (
          <>
            {section.questions.length > 0 ? (
              <>
                {mode === "edit" ? (
                  <AddQuestionButton onClick={addQuestion(0)}>
                    Add question
                  </AddQuestionButton>
                ) : null}
                {section.questions.map((question, index) => {
                  const otherQuestions = getOtherQuestions(state, question.id);
                  return (
                    <Flex key={index} direction="column" gap="2">
                      <QuestionForm
                        sectionType={section.type}
                        question={question}
                        schools={schools}
                        onRemove={onRemoveHandler}
                        onArchive={onArchiveHandler}
                        onUnarchive={onUnarchiveHandler}
                        onUpdate={onQuestionUpdateHandler}
                        otherQuestions={otherQuestions}
                      />
                      {mode === "edit" ? (
                        <AddQuestionButton onClick={addQuestion(index + 1)}>
                          Add question
                        </AddQuestionButton>
                      ) : null}
                    </Flex>
                  );
                })}
              </>
            ) : mode === "edit" ? (
              <Card background="gray.50">
                <Button
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={addQuestion(0)}
                  padding="16"
                >
                  <RiAddFill />
                  <Text size="sm" ml="2">
                    Add first question
                  </Text>
                </Button>
              </Card>
            ) : (
              <Card background="gray.50" alignItems="center" padding="8">
                <EmptyState
                  description="This section is empty"
                  graphic={null}
                />
              </Card>
            )}
          </>
        );
      }

      case AF.V2.SectionTypes.DisclaimerSection:
        return (
          <DisclaimerForm
            section={section}
            onUpdate={(updatedDisclaimer) => {
              dispatch({
                type: "UpdateSection",
                section: {
                  ...section,
                  ...updatedDisclaimer,
                },
              });
            }}
          />
        );

      case AF.V2.SectionTypes.SchoolRankingSection:
        return (
          <SchoolRankingForm
            section={section}
            onUpdate={(updatedSchoolRanking) => {
              if (!dispatch) {
                return;
              }

              dispatch({
                type: "UpdateSection",
                section: {
                  ...section,
                  ...updatedSchoolRanking,
                },
              });
            }}
          />
        );

      default:
        const _exhaustiveCheck: never = section;
        return _exhaustiveCheck;
    }
  }, [
    section,
    mode,
    addQuestion,
    state,
    schools,
    onRemoveHandler,
    onArchiveHandler,
    onUnarchiveHandler,
    onQuestionUpdateHandler,
    dispatch,
  ]);

  return (
    <>
      <Flex direction="column" gap={4}>
        {mode === "edit" && order === 0 ? (
          <AddSectionButton newOrder={order} />
        ) : null}
        <SectionChangesIndicator id={section.id} />
        <SectionForm section={section} />
        <ValidationAlert id={section.id} />
        <ImportError id={section.id} />
        {sectionElement}
        {mode === "edit" ? <AddSectionButton newOrder={order + 1} /> : null}
      </Flex>
      {confirmationDialog}
    </>
  );
};

function getOtherQuestions(state: State, questionId: uuid): AF.V2.Question[] {
  const otherQuestions: AF.V2.Question[] = state
    .get("draft")
    .sections.flatMap((section) => {
      return "questions" in section ? section.questions : [];
    })
    .filter((question) => question.id !== questionId);

  return otherQuestions;
}
